import { LogOut, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AiMark, BrandLockup, cn, UserAvatar } from '@kainos/ui';
import { NavItem } from '@/components/navigation/NavItem';
import { NAV_ITEMS } from '@/components/navigation/nav-items';
import { useAuth } from '@/lib/auth';
import { useUi } from '@/store/ui-store';

export interface SidebarProps {
  attentionCount?: number;
  /** Called after navigating, so the mobile drawer can close itself. */
  onNavigate?: () => void;
  className?: string;
}

/**
 * Primary navigation column: brand, sections, the AI entry point and the
 * signed-in user. Rendered once by the shell — never inside a page.
 */
export function Sidebar({ attentionCount, onNavigate, className }: SidebarProps) {
  const { user, signOut } = useAuth();
  const { openAiDrawer } = useUi();

  return (
    <aside
      className={cn(
        'flex h-full w-[236px] shrink-0 flex-col border-r border-line bg-surface',
        className,
      )}
    >
      <div className="px-5 pb-[18px] pt-[22px]">
        <Link to="/" onClick={onNavigate} aria-label="KainOS — Inicio">
          <BrandLockup />
        </Link>
      </div>

      <nav
        className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-2"
        aria-label="Navegación principal"
      >
        {NAV_ITEMS.map((item) => (
          <NavItem
            key={item.to}
            item={item}
            attentionCount={attentionCount}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      <div className="flex flex-col gap-2 border-t border-line p-3">
        <button
          type="button"
          onClick={() => openAiDrawer()}
          className={cn(
            'flex items-center gap-2.5 rounded-[10px] border border-primary-border bg-primary-soft',
            'px-3 py-2.5 text-[13px] font-bold text-brand transition-colors hover:bg-[#DBEAFE]',
          )}
        >
          <AiMark size={20} />
          Preguntar a KainOS AI
        </button>

        <Link
          to="/settings"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-[13px] font-semibold text-ink-muted transition-colors hover:bg-line-subtle"
        >
          <Settings size={17} strokeWidth={1.8} aria-hidden="true" />
          Configuración
        </Link>

        {user ? (
          <div className="flex items-center gap-2.5 px-3 py-2">
            <UserAvatar name={user.fullName} size={30} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-semibold text-ink">{user.fullName}</div>
              <div className="truncate text-[11px] font-medium text-ink-faint">
                {user.jobTitle ?? user.organizationName}
              </div>
            </div>
            <button
              type="button"
              onClick={signOut}
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-faint transition-colors hover:bg-line-subtle hover:text-ink-muted"
            >
              <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
